import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api, { fmtPrice, fmtDate } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Lock, CheckCircle2, AlertTriangle, FileText, Tag, X, HandCoins, Clock } from 'lucide-react';
import PayAnotherWayOption from '@/components/PayAnotherWayOption';
import { handleStripeDecline } from '@/lib/paymentDecline';
import { PaymentDeclineNotice } from '@/components/PaymentDeclineNotice';

/**
 * InvoicePayForm
 * --------------
 * Reusable inline-pay component for SplashPad-hosted invoice payment.
 * Mounts Stripe Elements scoped to the coach's connected account and
 * confirms a PaymentIntent against our internal /invoices endpoints.
 *
 * Powers two surfaces:
 *   1. `/pay-invoice/:invoice_id?token=...` — public page for the email-
 *      link recipient (no SplashPad account required).
 *   2. Parent dashboard Payments tab — opens in a Dialog modal, uses the
 *      authenticated /parent/invoices/{id}/intent + /confirm endpoints.
 *
 * Phase 43 — also supports redeeming a coach-issued promo code:
 *   - Parent enters code → form calls the same `intentUrl` with
 *     `?discount_code=...` which (re)creates the PaymentIntent at the
 *     discounted price and stamps the discount on the invoice.
 *   - Backend returns `intent_type='free'` + `already_paid` when the
 *     code zeroes out the invoice — in that case we short-circuit to
 *     the success view.
 *
 * Props:
 *   data         — initial payload from the `/intent` endpoint.
 *   confirmUrl   — POST URL for the /confirm endpoint.
 *   confirmExtra — extra fields merged into the confirm POST body
 *                  (e.g. { token } for the public flow).
 *   intentUrl    — GET URL for the /intent endpoint. Required if you
 *                  want the promo-code input to work (omit it to hide
 *                  the input).
 *   intentParams — query params merged into intentUrl refetches
 *                  (e.g. { token } for the public flow).
 *   onPaid       — optional callback fired once the invoice flips paid.
 *   compact      — when true, omit the coach branding header (used inside
 *                  the parent dashboard dialog where header is implied).
 *   externalUrl  — Phase 99: POST URL for the /request-external endpoint.
 *                  When provided AND the intent payload carries
 *                  `pay_another_way.enabled`, a "Pay another way" option
 *                  renders below the card form.
 *   externalExtra — extra fields merged into the request-external POST
 *                  body (e.g. { token } for the public flow).
 */
export default function InvoicePayForm({
    data: initialData,
    confirmUrl,
    confirmExtra = {},
    intentUrl,
    intentParams = {},
    onPaid,
    compact = false,
    externalUrl,
    externalExtra = {},
}) {
    const { t: t42 } = useLocaleR42();
    // Local data state so applying a promo code can update the
    // PaymentIntent + discount summary without prop drilling.
    const [data, setData] = useState(initialData);
    useEffect(() => { setData(initialData); }, [initialData]);

    // useMemo must be called unconditionally (React Hooks rule)
    const stripePromise = useMemo(() => {
        if (!data?.stripe_publishable_key || !data?.stripe_account_id) return null;
        return loadStripe(data.stripe_publishable_key, { stripeAccount: data.stripe_account_id });
    }, [data?.stripe_publishable_key, data?.stripe_account_id]);

    // Treat an already-paid invoice (status returned from /intent) as the
    // success state directly — saves a roundtrip and avoids accidentally
    // re-charging. Also catches the 100%-off "free" intent type.
    if (data?.invoice?.status === 'paid' || data?.already_paid) {
        return <InvoicePaidView invoice={{ ...data.invoice, status: 'paid' }} coach={data.coach} compact={compact} justNow={!!data.already_paid} />;
    }

    if (!stripePromise || !data?.client_secret) {
        return (
            <Card className="p-5 text-center" data-testid="invoice-pay-unavailable">
                <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-[#ff6b35]" />
                <p className="text-sm text-[#1a3a5c]"> {t42("Payment isn't available right now. Reach out to your coach so they can resend the invoice.")} </p>
            </Card>
        );
    }
    const appearance = {
        theme: 'stripe',
        variables: {
            colorPrimary: data.coach?.brand_color || '#00d4ff',
            borderRadius: '10px',
        },
    };
    return (
        <Elements
            stripe={stripePromise}
            options={{ clientSecret: data.client_secret, appearance }}
            key={data.client_secret}
        >
            <InvoicePayFormInner
                data={data}
                setData={setData}
                confirmUrl={confirmUrl}
                confirmExtra={confirmExtra}
                intentUrl={intentUrl}
                intentParams={intentParams}
                onPaid={onPaid}
                compact={compact}
                externalUrl={externalUrl}
                externalExtra={externalExtra}
            />
        </Elements>
    );
}

function InvoicePayFormInner({ data, setData, confirmUrl, confirmExtra, intentUrl, intentParams, onPaid, compact, externalUrl, externalExtra }) {
    const { t: t42 } = useLocaleR42();
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [decline, setDecline] = useState(null);
    const tryAnotherCard = () => {
        setDecline(null); setError('');
        try { elements?.getElement('payment')?.clear(); } catch { /* noop */ }
        try { elements?.getElement('payment')?.focus(); } catch { /* noop */ }
    };
    const [success, setSuccess] = useState(false);
    // Phase 157 — the card was charged but the server couldn't bind the
    // payment (409). Lock the Pay button so the family can't be billed twice.
    const [chargedUnmatched, setChargedUnmatched] = useState(false);
    // Phase 99 — offline payment state. Seeded from the intent payload so
    // a parent who already flagged the invoice sees the reminder banner.
    const paw = data?.pay_another_way || {};
    const [externalRequested, setExternalRequested] = useState(!!paw.requested);
    const [externalBusy, setExternalBusy] = useState(false);

    const requestExternal = async () => {
        if (!externalUrl) return;
        setExternalBusy(true);
        try {
            await api.post(externalUrl, { ...(externalExtra || {}) });
            setExternalRequested(true);
        } catch (err) {
            setError(err?.response?.data?.detail || "Couldn't switch to offline payment. Please try again or pay by card.");
        } finally {
            setExternalBusy(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setError('');
        setDecline(null);
        try {
            // Same page = no redirect. `if_required` lets Stripe still
            // bounce out for 3DS / Klarna / Bank flows when needed.
            const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: window.location.href },
                redirect: 'if_required',
            });
            if (confirmErr) {
                // Phase 178 — plain-English reason + tell the coach.
                setDecline(handleStripeDecline(confirmErr, {
                    context: 'invoice',
                    instructorId: data?.coach?.id || data?.invoice?.instructor_id,
                    coachSlug: data?.coach?.slug,
                    invoiceId: data?.invoice?.id,
                    amountCents: data?.invoice?.amount_due_cents ?? data?.invoice?.amount_cents ?? data?.invoice?.total_cents,
                    packageName: data?.invoice?.description || data?.invoice?.title || 'Invoice',
                    parentEmail: data?.invoice?.parent_email,
                }));
                setSubmitting(false);
                return;
            }
            // Finalize server-side. Idempotent — webhook also handles it.
            await api.post(confirmUrl, {
                payment_intent_id: paymentIntent?.id,
                ...(confirmExtra || {}),
            });
            setSuccess(true);
            if (onPaid) onPaid();
        } catch (err) {
            // The PaymentIntent may have actually succeeded even if our
            // /confirm endpoint had a hiccup — the webhook will reconcile.
            // Still surface a friendly message so the payer knows.
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409) {
                // Phase 157 — charged but unmatched. Never let them retry here.
                setChargedUnmatched(true);
                setError(detail || "Your card was charged, but we couldn't match the payment automatically. Please don't pay again — your coach has been notified.");
            } else {
                setError(detail || 'Something went wrong confirming your payment. If the charge appears on your card, your coach will see it within a minute.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return <InvoicePaidView invoice={{ ...data.invoice, status: 'paid' }} coach={data.coach} compact={compact} justNow />;
    }

    const inv = data.invoice;
    const discount = data.discount?.applied ? data.discount : null;
    const amountAfter = discount ? discount.amount_after_cents : (inv?.amount_cents || 0);

    return (
        <div className={compact ? '' : 'mx-auto max-w-lg space-y-4'} data-testid="invoice-pay-form">
            {/* Coach branding header (omitted in compact / dialog mode) */}
            {!compact && (
                <div className="flex items-center gap-3 px-1">
                    {data.coach?.logo_url ? (
                        <img src={data.coach.logo_url} alt={data.coach.display_name} className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                        <div className="h-10 w-10 rounded-full bg-[#00d4ff]/20" />
                    )}
                    <div>
                        <div className="text-xs uppercase tracking-wide text-[#1a3a5c]/70">{t42("Invoice from your coach")}</div>
                        <div className="font-display text-lg font-bold text-[#0a1628]">{data.coach?.display_name || 'Your coach'}</div>
                    </div>
                </div>
            )}

            {/* Invoice summary */}
            <Card className="p-5" data-testid="invoice-pay-summary">
                <div className="flex items-start gap-3">
                    <div className="rounded-xl bg-[#00d4ff]/10 p-2.5 text-[#0a1628]">
                        <FileText className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <Badge variant="outline" className="border-transparent bg-[#0a1628]/8 text-[#0a1628]">{t42("Invoice")}</Badge>
                        <div className="mt-1 font-display text-lg font-bold text-[#0a1628]" data-testid="invoice-pay-subject">
                            {inv?.subject || 'Invoice'}
                        </div>
                        {inv?.body && (
                            <p className="mt-1 whitespace-pre-wrap text-sm text-[#1a3a5c]/80">{inv.body}</p>
                        )}
                        <div className="mt-3 flex items-baseline gap-2">
                            {discount && (
                                <span className="text-base text-[#1a3a5c]/60 line-through" data-testid="invoice-pay-amount-original">
                                    {fmtPrice(inv?.amount_cents || 0, inv?.currency)}
                                </span>
                            )}
                            <span className="font-display text-2xl font-bold text-[#0a1628]" data-testid="invoice-pay-amount">
                                {fmtPrice(amountAfter, inv?.currency)}
                            </span>
                        </div>
                        {discount && (
                            <p className="mt-1 text-xs font-semibold text-[#00b87a]" data-testid="invoice-pay-discount-line"> {t42("Code")} <code className="font-mono">{discount.code}</code> {t42("— you saved")} {fmtPrice(discount.amount_discounted_cents, discount?.currency)}
                            </p>
                        )}
                        {inv?.due_date && (
                            <p className="mt-1 text-xs text-[#1a3a5c]/70">{t42("Due")} {fmtDate(inv.due_date)}</p>
                        )}
                    </div>
                </div>
            </Card>

            {/* Promo code input (only when we know how to refetch the intent) */}
            {intentUrl && (
                <PromoCodeInput
                    intentUrl={intentUrl}
                    intentParams={intentParams}
                    applied={discount}
                    onApplied={(updated) => setData(updated)}
                />
            )}

            {/* Stripe Elements pay form */}
            <Card className="p-5">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <div className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[#1a3a5c]/70">
                            <Lock className="h-3 w-3" /> {t42("Payment details")} </div>
                        <PaymentElement options={{ layout: 'tabs' }} />
                    </div>
                    {decline && (
                        <PaymentDeclineNotice
                            decline={decline}
                            idPrefix="invoice-decline"
                            onTryAnother={tryAnotherCard}
                            onRetry={() => setDecline(null)}
                            onPayAnotherWay={(!externalRequested && externalUrl && paw.enabled) ? requestExternal : undefined}
                        />
                    )}
                    {error && !decline && (
                        <div
                            className={`rounded-lg border p-3 text-sm text-[#0a1628] ${chargedUnmatched ? 'border-[#ffd700]/60 bg-[#fffbeb] font-medium' : 'border-[#ff6b35]/40 bg-[#fff6f0]'}`}
                            data-testid={chargedUnmatched ? 'invoice-pay-charged-unmatched' : 'invoice-pay-error'}
                            role="alert"
                        >
                            {error}
                        </div>
                    )}
                    {/* Phase 0bv — locked card-on-file consent disclosure. */}
                    <div
                        className="flex items-start gap-2 rounded-lg border border-[#00d4ff]/25 bg-[#e6f9fd] p-3 text-xs text-[#0a1628]"
                        data-testid="invoice-pay-card-on-file-notice"
                    >
                        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#00d4ff]" aria-hidden="true" />
                        <span className="leading-relaxed">
                            <strong>{t42("Your card will be saved on file.")}</strong>{' '} {t42("By paying, you authorize")} {data.coach?.display_name || 'your coach'} {t42("to save this card and charge it for future lessons or packages. Manage saved cards anytime under")} <span className="font-medium">{t42("Payment methods")}</span> {t42("in your parent portal.")} </span>
                    </div>
                    <Button
                        type="submit"
                        disabled={!stripe || !elements || submitting || chargedUnmatched}
                        className="sd-cta sd-focus h-12 w-full rounded-xl"
                        data-testid="invoice-pay-submit"
                    >
                        {submitting
                            ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> {t42("Confirming…")}</>
                            : chargedUnmatched
                                ? <>{t42("Payment received — don't pay again")}</>
                                : <>{t42("Pay")} {fmtPrice(amountAfter, inv?.currency)}</>}
                    </Button>
                    <p className="text-center text-xs text-[#1a3a5c]/65"> {t42("Secured by Stripe. Your card details never touch SplashPad.")} </p>
                </form>
            </Card>

            {/* === Phase 99 — Pay another way (Venmo / Zelle / cash) ========= */}
            {externalRequested && (
                <Card className="border-[#ffd700]/50 bg-[#fffbeb] p-4" data-testid="invoice-pay-external-pending">
                    <div className="flex items-start gap-3">
                        <HandCoins className="mt-0.5 h-5 w-5 shrink-0 text-[#0a1628]" />
                        <div className="min-w-0 flex-1">
                            <div className="font-semibold text-[#0a1628]">{t42("You're paying another way")}</div>
                            <p className="mt-0.5 text-xs text-[#1a3a5c]/85"> {t42("Send your payment to")} {data.coach?.display_name || 'your coach'} {t42("using the details below. They'll confirm once it arrives — until then this stays pending.")} </p>
                            {paw.instructions && (
                                <div className="mt-2 whitespace-pre-wrap rounded-lg bg-white/70 p-2.5 text-sm text-[#1a3a5c]" data-testid="invoice-pay-external-instructions">
                                    {paw.instructions}
                                </div>
                            )}
                            <div className="mt-2 flex items-center gap-1.5 text-xs text-[#1a3a5c]/70">
                                <Clock className="h-3.5 w-3.5" /> {t42("Changed your mind? You can still pay by card above.")} </div>
                        </div>
                    </div>
                </Card>
            )}
            {!externalRequested && externalUrl && paw.enabled && (
                <PayAnotherWayOption
                    idPrefix="invoice-pay"
                    instructions={paw.instructions}
                    coachDisplayName={data.coach?.display_name}
                    confirmLabel={externalBusy ? 'Saving…' : "Confirm — I'll pay the coach directly"}
                    onConfirm={requestExternal}
                />
            )}
        </div>
    );
}

function PromoCodeInput({ intentUrl, intentParams, applied, onApplied }) {
    const { t: t42 } = useLocaleR42();
    const [code, setCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleApply = async (e) => {
        if (e) e.preventDefault();
        const trimmed = code.trim().toUpperCase();
        if (!trimmed) {
            setError('Enter a code first.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const r = await api.get(intentUrl, {
                params: { ...(intentParams || {}), discount_code: trimmed },
            });
            const next = r.data || {};
            if (!next.discount?.applied && !next.already_paid) {
                // Server returned without applying — surface a generic
                // message (shouldn't happen since invalid codes 400 out).
                setError("We couldn't apply that code. Double-check the spelling.");
            } else {
                onApplied(next);
                setCode('');
            }
        } catch (err) {
            setError(err?.response?.data?.detail || "We couldn't apply that code. Double-check the spelling.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async () => {
        setSubmitting(true);
        setError('');
        try {
            const r = await api.get(intentUrl, { params: { ...(intentParams || {}) } });
            onApplied(r.data || {});
            setCode('');
        } catch (err) {
            setError(err?.response?.data?.detail || "Couldn't remove the discount. Refresh and try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (applied) {
        return (
            <Card className="flex items-center justify-between gap-3 border-[#00b87a]/40 bg-[#ecfaf2] p-3" data-testid="invoice-pay-discount-applied">
                <div className="flex items-center gap-2 min-w-0">
                    <Tag className="h-4 w-4 shrink-0 text-[#00b87a]" />
                    <div className="min-w-0">
                        <div className="font-mono text-sm font-bold text-[#0a1628] truncate" data-testid="invoice-pay-discount-code">
                            {applied.code}
                        </div>
                        <div className="text-xs text-[#1a3a5c]/80">
                            {fmtPrice(applied.amount_discounted_cents, applied?.currency)} {t42("off applied")} </div>
                    </div>
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemove}
                    disabled={submitting}
                    className="shrink-0 text-[#1a3a5c]/70 hover:bg-[#0a1628]/5 hover:text-[#0a1628]"
                    data-testid="invoice-pay-discount-remove"
                >
                    {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><X className="mr-1 h-3 w-3" /> {t42("Remove")}</>}
                </Button>
            </Card>
        );
    }

    return (
        <Card className="p-4" data-testid="invoice-pay-discount-card">
            <form onSubmit={handleApply} className="space-y-2">
                <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[#1a3a5c]/70">
                    <Tag className="h-3 w-3" /> {t42("Have a discount code?")} </div>
                <div className="flex gap-2">
                    <Input
                        value={code}
                        onChange={(e) => { setCode(e.target.value.toUpperCase()); if (error) setError(''); }}
                        placeholder={t42("ENTER CODE")}
                        maxLength={40}
                        className="h-11 flex-1 rounded-xl font-mono uppercase"
                        disabled={submitting}
                        data-testid="invoice-pay-discount-input"
                    />
                    <Button
                        type="submit"
                        variant="outline"
                        disabled={submitting || !code.trim()}
                        className="h-11 rounded-xl"
                        data-testid="invoice-pay-discount-apply"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t42("Apply")}
                    </Button>
                </div>
                {error && (
                    <p className="text-xs text-[#ff6b35]" data-testid="invoice-pay-discount-error">{error}</p>
                )}
            </form>
        </Card>
    );
}

/**
 * InvoiceDiscountOnlyFallback
 * ---------------------------
 * Rendered when the initial `/intent` request returned 503 ("coach
 * hasn't finished setting up payments"). The coach can't accept card
 * charges, but if the parent has a 100%-off discount code from the
 * coach, that path goes through `_create_invoice_payment_intent`'s
 * short-circuit (no Stripe call) and still works. So we surface a
 * discount-code-only fallback form rather than a hard dead end.
 *
 * On success (free intent_type or already_paid), we flip into
 * InvoicePaidView. Otherwise we surface the backend's error message
 * inline.
 */
export function InvoiceDiscountOnlyFallback({ intentUrl, intentParams = {}, compact = false }) {
    const { t: t42 } = useLocaleR42();
    const [code, setCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [paidData, setPaidData] = useState(null);

    if (paidData) {
        return <InvoicePaidView invoice={{ ...paidData.invoice, status: 'paid' }} coach={paidData.coach} compact={compact} justNow />;
    }

    const handleApply = async (e) => {
        if (e) e.preventDefault();
        const trimmed = code.trim().toUpperCase();
        if (!trimmed) {
            setError('Enter a code first.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            // Preflight: /validate-discount works WITHOUT Stripe Connect
            // (it just does the math). This lets us catch both invalid
            // codes AND valid-but-partial codes cleanly before triggering
            // the /intent endpoint which would 503 on partial discounts.
            const validateUrl = intentUrl.replace(/\/intent$/, '/validate-discount');
            const v = await api.post(
                validateUrl,
                { code: trimmed },
                { params: { ...(intentParams || {}) } },
            );
            const vd = v.data || {};
            if (!vd.applied) {
                setError("We couldn't apply that code. Double-check the spelling.");
                setSubmitting(false);
                return;
            }
            if ((vd.amount_after_cents || 0) > 0) {
                // Valid code, but doesn't fully cover the invoice. The
                // coach has no Connect so the remainder can't be charged.
                setError("This code reduces the invoice but doesn't cover it fully. Your coach needs to finish setting up payments before you can pay the balance.");
                setSubmitting(false);
                return;
            }
            // Code zeros out the invoice → hit /intent to trigger the
            // server-side short-circuit (records redemption + marks paid).
            const r = await api.get(intentUrl, {
                params: { ...(intentParams || {}), discount_code: trimmed },
            });
            const next = r.data || {};
            if (next.already_paid || next.intent_type === 'free') {
                setPaidData(next);
            } else {
                setError("Something went wrong applying that code. Please try again.");
            }
        } catch (err) {
            setError(err?.response?.data?.detail || "We couldn't apply that code. Double-check the spelling.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={compact ? '' : 'mx-auto max-w-lg space-y-4'} data-testid="invoice-pay-discount-fallback">
            <Card className="p-5" data-testid="invoice-pay-discount-fallback-banner">
                <div className="flex items-start gap-3">
                    <div className="rounded-xl bg-[#ff6b35]/15 p-2.5 text-[#ff6b35]">
                        <AlertTriangle className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="font-display text-base font-bold text-[#0a1628]">{t42("Card payment isn't available right now")}</h2>
                        <p className="mt-1 text-sm text-[#1a3a5c]/85"> {t42("Your coach hasn't finished connecting their payment account yet. If they sent you a discount code that covers this invoice in full, you can still apply it below.")} </p>
                    </div>
                </div>
            </Card>

            <Card className="p-4">
                <form onSubmit={handleApply} className="space-y-2">
                    <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[#1a3a5c]/70">
                        <Tag className="h-3 w-3" /> {t42("Have a discount code?")} </div>
                    <div className="flex gap-2">
                        <Input
                            value={code}
                            onChange={(e) => { setCode(e.target.value.toUpperCase()); if (error) setError(''); }}
                            placeholder={t42("ENTER CODE")}
                            maxLength={40}
                            className="h-11 flex-1 rounded-xl font-mono uppercase"
                            disabled={submitting}
                            data-testid="invoice-pay-discount-fallback-input"
                        />
                        <Button
                            type="submit"
                            variant="outline"
                            disabled={submitting || !code.trim()}
                            className="h-11 rounded-xl"
                            data-testid="invoice-pay-discount-fallback-apply"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t42("Apply")}
                        </Button>
                    </div>
                    {error && (
                        <p className="text-xs text-[#ff6b35]" data-testid="invoice-pay-discount-fallback-error">{error}</p>
                    )}
                </form>
            </Card>
        </div>
    );
}

function InvoicePaidView({ invoice, coach, compact, justNow }) {
    const { t: t42 } = useLocaleR42();
    return (
        <div className={compact ? '' : 'mx-auto max-w-lg'} data-testid="invoice-paid-view">
            <Card className="p-6 text-center">
                <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-[#00b87a]" />
                <h2 className="font-display text-xl font-bold text-[#0a1628]">
                    {justNow ? t42("Payment received — thanks!") : t42("This invoice is already paid")}
                </h2>
                <p className="mt-2 text-sm text-[#1a3a5c]/85">
                    {justNow
                        ? `Your payment of ${fmtPrice(invoice?.amount_cents || 0, invoice?.currency)} to ${coach?.display_name || 'your coach'} has been received. A receipt is on its way to your email.`
                        : `${invoice?.subject || 'This invoice'} was paid on ${fmtDate(invoice?.paid_at) || 'an earlier date'}. No further action needed.`}
                </p>
            </Card>
        </div>
    );
}
